import { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { pages } from "../../../constants/PagePaths";
import { api } from "../../../config/api";
import { util } from "../../../config/util";
import { Cookies } from "../../../constants/Cookies";
import { FreeUseDay } from "../../../constants/General";
import styles from "../mainRedesign.module.scss";

const PROJECT_PERMISSIONS = ["STATEMENT", "ESTIMATE", "OTHER_STATEMENT"];
const EXCEL_PERMISSIONS = ["EXCEL_X2", "EXCEL_CONTRACT", "EXCEL_FLUCTUATION", "EXCEL_REPORT", "EXCEL_READY"];

type CtaType = "signup" | "purchase" | "excel" | "project";

export default () => {
  const history = useHistory();
  const [ctaType, setCtaType] = useState<CtaType>(util.isLogin() ? "project" : "signup");
  const [notice, setNotice] = useState<{ title: string; link: string } | null>(null);
  const [tutorialLoading, setTutorialLoading] = useState(false);
  const [showTutorialChoice, setShowTutorialChoice] = useState(false);

  useEffect(() => {
    // 공지 id 43 제목 가져오기
    api.getNotice({ id: 43 }).then((res) => {
      const n = res?.data?.notice;
      if (n) {
        setNotice({ title: n.title, link: `/notice/${n.id}` });
      }
    }).catch(() => {});

    if (util.isLogin()) {
      api.getMyInfo().then((res) => {
        const now = new Date();
        const active = (res.data.expiry_dates || []).filter((e) => {
          if (!e.expiry_date) return false;
          return new Date(e.expiry_date) > now;
        });
        const hasProject = active.some((e) => PROJECT_PERMISSIONS.includes(e.name));
        const hasExcel = active.some((e) => EXCEL_PERMISSIONS.includes(e.name));

        if (hasProject) setCtaType("project");
        else if (hasExcel) setCtaType("excel");
        else if (active.length > 0) setCtaType("project");
        else setCtaType("purchase");
      }).catch(() => {});
    }
  }, []);

  const startTutorial = async (template: "일반" | "건축") => {
    const key = util.getCookie(Cookies.authKey);
    if (!key) {
      alert("로그인이 필요합니다.");
      return;
    }
    setTutorialLoading(true);
    try {
      const title = template === "건축" ? "튜토리얼 - 건축공사" : "튜토리얼 - 건설공사";
      const res: any = await api.createProject(title, false, false, false, "", "", template);
      const projectId = res?.data?.id || res?.data?.project?.id;
      if (!projectId) {
        alert("프로젝트 생성에 실패했습니다.");
        setTutorialLoading(false);
        return;
      }
      const costUrl = process.env.REACT_APP_COST_URL || "http://localhost:4200/";
      window.location.href = `${costUrl}document/${window.btoa(String(projectId))}?tutorial=landscape`;
    } catch (e: any) {
      console.error("Tutorial project creation failed:", e);
      alert("프로젝트 생성에 실패했습니다. 다시 시도해주세요.");
      setTutorialLoading(false);
    }
  };

  return (
    <section className={styles.hero}>
      <div className={styles.heroInner}>
        {notice && (
          <Link to={notice.link} className={styles.heroNotice}>
            <span className={styles.heroNoticeBadge}>공지</span>
            {notice.title}
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
              chevron_right
            </span>
          </Link>
        )}

        <h1 className={styles.heroTitle}>
          건설 서류, AI로 쉽고 빠르게
        </h1>
        <p className={styles.heroSubtitle}>
          설계내역서부터 엑셀 공무까지, 콘엑스 하나로 완성하세요.
          <br />
          대한민국 1호 AI 적산설계 프로그램
        </p>

        <div className={styles.heroCta}>
          <button
            className={styles.btnPrimary}
            onClick={() => {
              switch (ctaType) {
                case "signup": history.push(pages.signup); break;
                case "purchase": history.push(pages.payment); break;
                case "excel": history.push(pages.excel); break;
                case "project": history.push(pages.costprojects + "/normal"); break;
              }
            }}
          >
            {{
              signup: `${FreeUseDay}일 무료체험 시작`,
              purchase: "이용권 구매하기",
              excel: "엑셀자동화 사용하기",
              project: "내 프로젝트로 이동",
            }[ctaType]}
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              arrow_forward
            </span>
          </button>
          {ctaType === "project" && (
            <button
              className={styles.btnOutline}
              onClick={() => setShowTutorialChoice((v) => !v)}
            >
              10분 튜토리얼 시작
            </button>
          )}
        </div>

        {showTutorialChoice && !tutorialLoading && (
          <div className={styles.tutorialOverlay}>
            <div className={styles.tutorialChoiceRow}>
              <button className={styles.tutorialBtn} onClick={() => startTutorial("일반")}>
                <div className={styles.tutorialBtnTitle}>일반 구조</div>
                <div className={styles.tutorialBtnDesc}>토목 / 조경 / 설비 등 (공사 → 공종 → 품명)</div>
              </button>
              <button className={styles.tutorialBtn} onClick={() => startTutorial("건축")}>
                <div className={styles.tutorialBtnTitle}>건축 구조</div>
                <div className={styles.tutorialBtnDesc}>건축공사 (공사 → 품명)</div>
              </button>
              <button className={styles.tutorialCancel} onClick={() => setShowTutorialChoice(false)}>
                취소
              </button>
            </div>
          </div>
        )}

        {tutorialLoading && (
          <div className={styles.tutorialOverlay}>
            <div className={styles.tutorialLoading}>튜토리얼 프로젝트 준비 중...</div>
          </div>
        )}

        <div className={styles.trustIndicators}>
          <div className={styles.trustItem}>
            <span className={styles.trustCheck}>✓</span>
            업무시간 96% 단축
          </div>
          <div className={styles.trustItem}>
            <span className={styles.trustCheck}>✓</span>
            2,140+ 용역 실적
          </div>
          {ctaType === "signup" && (
            <div className={styles.trustItem}>
              <span className={styles.trustCheck}>✓</span>
              가입 즉시 {FreeUseDay}일 무료
            </div>
          )}
        </div>

      </div>
    </section>
  );
};
